import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

interface PagedResponse {
  data: string[];
  message: string;
  extraParam: { totalCount: number };
}

const SUBURB_PAGE_SIZE = 100;

interface PostcodeResponse {
  data: number[];
  message: string;
}

// Hardcoded since there are only 8 Australian states/territories and they never change
const STATE_ABBREVIATIONS: Record<string, string> = {
  'australian capital territory': 'ACT',
  'new south wales': 'NSW',
  'northern territory': 'NT',
  'queensland': 'QLD',
  'south australia': 'SA',
  'tasmania': 'TAS',
  'victoria': 'VIC',
  'western australia': 'WA',
};

const KNOWN_ABBRS = new Set(Object.values(STATE_ABBREVIATIONS));

@Injectable({ providedIn: 'root' })
export class PostcodeValidationService {
  private postcodeCache = new Map<string, string[]>();

  constructor(private http: HttpClient) {}

  isKnownState(state: string): boolean {
    return KNOWN_ABBRS.has(this.toAbbreviation(state));
  }

  suburbInList(list: string[], suburb: string): boolean {
    const term = suburb.trim().toLowerCase();
    return list.some(opt => opt.toLowerCase() === term);
  }

  findSuburb(list: string[], suburb: string): string | undefined {
    const term = suburb.trim().toLowerCase();
    return list.find(opt => opt.toLowerCase() === term);
  }

  toAbbreviation(state: string): string {
    const trimmed = state.trim();
    const upper = trimmed.toUpperCase();
    if (KNOWN_ABBRS.has(upper)) return upper;
    return STATE_ABBREVIATIONS[trimmed.toLowerCase()] ?? upper;
  }

  async fetchSuburbsPage(
    state: string,
    page: number,
    search?: string
  ): Promise<{ items: string[]; hasMore: boolean }> {
    const abbr = this.toAbbreviation(state);
    const q = search?.trim();
    const searchParam =
      q && q.length > 0 ? `&search=${encodeURIComponent(q)}` : '';
    const res = await this.http
      .get<PagedResponse>(
        `${environment.apiConfig.uri}/PostCode/cities?pageNumber=${page}&pageSize=${SUBURB_PAGE_SIZE}&state=${encodeURIComponent(abbr)}${searchParam}`
      )
      .toPromise();
    const items = res?.data ?? [];
    const totalCount = res?.extraParam?.totalCount ?? items.length;
    const hasMore = page * SUBURB_PAGE_SIZE < totalCount;
    return { items, hasMore };
  }

  async getPostcodesBySuburbAndState(suburb: string, state: string): Promise<string[]> {
    const abbr = this.toAbbreviation(state);
    const s = suburb.trim().toUpperCase();
    const key = `${abbr}:${s}`;

    if (this.postcodeCache.has(key)) return this.postcodeCache.get(key)!;

    const res = await this.http
      .get<PostcodeResponse>(
        `${environment.apiConfig.uri}/PostCode/postcodeByCityAndState?city=${encodeURIComponent(s)}&state=${abbr}`
      )
      .toPromise();

    const postcodes = (res?.data ?? []).map(p => p.toString()).sort();
    this.postcodeCache.set(key, postcodes);
    return postcodes;
  }

}
